import { isNil } from 'lodash-es'

import { CHRT_HIGH_RISK_GROUP, HIGH_ALERT_QUESTION_KEYS } from './constants'
import { MESSAGE_LOCATION } from '../../features/common/constants/mixpanelConstants'
import { Assignment, AssignmentGroup, Response } from '../../models/assignments/Assignments'
import { QuestionResponse } from '../../models/outcome/Outcome'
import { ProgramNames } from '../../models/programs/Programs'
import { RiskFactorsResponse } from '../../models/riskFactors/RiskFactors'
import { customContentAttributes } from '../assignments/customContentAttributes'
import { isAssessmentSummaryStage } from '../assignments/utils'

export const getCHRTHighRiskDetails = (responseDetails?: QuestionResponse[] | null): QuestionResponse[] =>
  responseDetails?.filter((detail) => detail.groups?.includes(CHRT_HIGH_RISK_GROUP)) ?? []

export const hasCHRTHighRisk = (responseDetails?: QuestionResponse[] | null): boolean =>
  getCHRTHighRiskDetails(responseDetails).length > 0

export const isHighAlert = (values: Response, programName: string) => {
  // TODO: Come up with a better way to control isHigahAlert checks to not depend on program name
  const isTeensProgram = programName === ProgramNames.TeensTherapy
  const isTherapyProgram = programName === ProgramNames.BlendedCareTherapy
  const isDBTProgram = programName === ProgramNames.DialecticalBehaviorTherapy

  const shouldCheckCheckIn = isTherapyProgram || isTeensProgram || isDBTProgram
  const shouldCheckCHRT = isTherapyProgram || isTeensProgram || isDBTProgram
  const shouldCheckRCADS = isTeensProgram
  const shouldCheckIntake = isTeensProgram

  const {
    PHQ9_SI,
    PHQ9_SI_PLAN,
    PHQ9_SI_INTENT,
    CHECK_IN_SI,
    CHECK_IN_SELF_HARM,
    CHRT_SI_PLAN,
    CHRT_SI_THOUGHTS,
    CHRT_SI_THOUGHTS_HOW,
    RCADS_THOUGHTS_ABOUT_DEATH,
    CLIENT_INTAKE_ATTEMPTED_SUICIDE,
    CLIENT_INTAKE_SELF_INJURY,
  } = HIGH_ALERT_QUESTION_KEYS

  const hasIntakeSI =
    shouldCheckIntake && (values[CLIENT_INTAKE_ATTEMPTED_SUICIDE] === 1 || values[CLIENT_INTAKE_SELF_INJURY] === 1)

  const hasPHQ9BaselineSI = values[PHQ9_SI] > 0
  const hasPHQ9ActiveSI = values[PHQ9_SI_PLAN] > 0 || values[PHQ9_SI_INTENT] > 0

  const hasRCADSSI = shouldCheckRCADS && values[RCADS_THOUGHTS_ABOUT_DEATH] > 0

  const isPassiveSI = hasPHQ9BaselineSI && values[PHQ9_SI_PLAN] === 0 && values[PHQ9_SI_INTENT] === 0
  const isActiveSI = hasPHQ9ActiveSI || hasRCADSSI || hasIntakeSI
  const isSI = isActiveSI || isPassiveSI

  const isActiveSICheckIn = shouldCheckCheckIn && (values[CHECK_IN_SI] > 5 || values[CHECK_IN_SELF_HARM] > 5)

  const isActiveSICHRT =
    shouldCheckCHRT && (values[CHRT_SI_PLAN] > 0 || values[CHRT_SI_THOUGHTS] > 0 || values[CHRT_SI_THOUGHTS_HOW] > 0)

  return { isActiveSI, isPassiveSI, isSI, isActiveSICheckIn, isActiveSICHRT }
}

export const hasPastSI = (response: RiskFactorsResponse) => {
  return (
    response.is_high_alert_email_sent ||
    response.clinical_factors?.safety_alert_indicated ||
    response.clinical_factors?.self_harm_indicated ||
    response.clinical_factors?.suicidal_ideation_indicated
  )
}

export const getPostActivityScreen = ({
  values,
  programName,
  selectedAssignment,
  postRequisiteAssignment,
  bypassAssessmentSummary = true,
  linkedAssignmentsCount,
  IS_WEB_APP,
}: {
  values: Response
  programName: string
  selectedAssignment: Assignment | null
  postRequisiteAssignment?: Assignment
  bypassAssessmentSummary?: boolean
  // Number of members of this assignment's linked group that are actually assigned. A
  // group resolving to a single present member means the assignment is effectively standalone.
  linkedAssignmentsCount?: number
  IS_WEB_APP?: boolean
}) => {
  const { isActiveSI, isPassiveSI, isSI, isActiveSICheckIn, isActiveSICHRT } = isHighAlert(values, programName)
  const isAssessment = selectedAssignment?.content.group === AssignmentGroup.ASSESSMENT
  const isSummaryPage = isAssessment && isAssessmentSummaryStage(selectedAssignment) && !isSI
  const exerciseSubmitted =
    selectedAssignment?.content.group === AssignmentGroup.EXERCISE && !isSI && !isActiveSICheckIn
  const checkInSubmittedWithSIOrSelfHarm = isActiveSICheckIn
  const CHRTSubmittedWithSI = isActiveSICHRT

  // An assignment that belongs to a linked-onboarding group but is the only member actually
  // assigned (e.g. Performance coaching assigns bccCareConsent without the bccClientIntake/
  // bccBaselineMetadata siblings) should behave as a standalone activity. We check the present
  // member count explicitly rather than inferring it from a missing postrequisite, so the last
  // sibling of a full group (which also has no postrequisite) is not misclassified as standalone.
  const linkedStatusAssignments = selectedAssignment
    ? customContentAttributes[selectedAssignment.content.name]?.linkedStatusAssignments
    : undefined
  const isLoneLinkedAssignment = !isNil(linkedStatusAssignments) && linkedAssignmentsCount === 1

  // We are prioritizing CHRT SI questions then phq9_9_x, the suicidal ideation/self harm questions.
  if (isActiveSICHRT) {
    // We need to navigate from the parent screen to pass the params through
    return {
      screen: 'Activities',
      params: {
        screen: 'CurrentActivitiesScreen',
        params: { CHRTSubmittedWithSI },
      },
    }
  } else if (isAssessment && isSI) {
    // Navigate to CurrentActivitiesScreen with HighAlertContactCard modal on 1lyra
    return IS_WEB_APP
      ? {
          screen: 'Activities',
          params: {
            screen: 'CurrentActivitiesScreen',
            params: {
              isAssessment,
              isSI,
              isActiveSI,
              isPassiveSI,
            },
          },
        }
      : {
          screen: 'HighAlertContactCard',
          params: {
            isActiveSI,
            isPassiveSI,
            cameFrom: MESSAGE_LOCATION.POST_ASSESSMENT,
            exerciseSubmitted,
            checkInSubmittedWithSIOrSelfHarm,
          },
        }
  } else if (isLoneLinkedAssignment && bypassAssessmentSummary) {
    return { screen: undefined, params: undefined }
  } else if (isNil(postRequisiteAssignment) || !bypassAssessmentSummary) {
    const route = () => {
      if (isAssessment) {
        if (isSummaryPage) {
          return 'SummaryPage'
        } else {
          return 'Activities'
        }
      }
      return 'ActivityOverview'
    }
    return {
      screen: route(),
      params: {
        isActiveSI,
        isPassiveSI,
        cameFrom: MESSAGE_LOCATION.POST_ASSESSMENT,
        exerciseSubmitted,
        checkInSubmittedWithSIOrSelfHarm,
      },
    }
  }
  return { screen: undefined, params: undefined }
}
